import type { FormEvent } from 'react';
import { useState } from 'react';

import {
  BlockConfirmButton,
  Button,
  ConfirmActionButton,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  PageLayout,
  Tabs,
  UserCard,
} from '../../components/index.js';
import { useBlockUser, useBlockedUsers, useUnblockUser } from '../../lib/blocks.js';
import { getErrorMessage } from '../../lib/errors.js';
import { formatDate } from '../../lib/format-date.js';
import {
  useAcceptFriendRequest,
  useCancelFriendRequest,
  useDeclineFriendRequest,
  useFriends,
  useIncomingFriendRequests,
  useOutgoingFriendRequests,
  useRemoveFriend,
  useSendFriendRequest,
} from '../../lib/friends.js';
import { useUserSearch } from '../../lib/users.js';

function FriendsListSection() {
  const friends = useFriends();
  const removeFriend = useRemoveFriend();

  if (friends.isPending) return <LoadingState label="Loading friends…" />;
  if (friends.isError) {
    return (
      <ErrorState message={getErrorMessage(friends.error)} onRetry={() => void friends.refetch()} />
    );
  }
  if (friends.data.length === 0) {
    return <p className="text-textSecondary">You haven&apos;t added any friends yet.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {friends.data.map((friend) => (
        <UserCard
          key={friend.user.id}
          user={friend.user}
          meta={`Friends since ${formatDate(friend.friendedAt)}`}
          actions={
            // Removing a friend is destructive and not undoable without the
            // other player accepting a fresh request, so it goes through the
            // shared confirmation. `mutateAsync` (not `mutate`) is what keeps
            // the dialog open until the removal has actually happened and
            // stops a second submission while it is in flight.
            <ConfirmActionButton
              triggerLabel="Remove"
              triggerVariant="secondary"
              triggerAriaLabel={`Remove ${friend.user.username} from your friends`}
              title="Remove friend?"
              description={`Are you sure you want to remove ${friend.user.username} from your friends?`}
              confirmLabel="Remove Friend"
              destructive
              isPending={removeFriend.isPending && removeFriend.variables === friend.user.id}
              onConfirm={() => removeFriend.mutateAsync(friend.user.id)}
            />
          }
        />
      ))}
      {removeFriend.isError && <ErrorState message={getErrorMessage(removeFriend.error)} />}
    </div>
  );
}

function IncomingRequestsSection() {
  const incoming = useIncomingFriendRequests();
  const accept = useAcceptFriendRequest();
  const decline = useDeclineFriendRequest();

  if (incoming.isPending) return <LoadingState label="Loading requests…" />;
  if (incoming.isError) {
    return (
      <ErrorState
        message={getErrorMessage(incoming.error)}
        onRetry={() => void incoming.refetch()}
      />
    );
  }
  if (incoming.data.length === 0) {
    return <p className="text-textSecondary">No incoming friend requests.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {incoming.data.map((request) => (
        <UserCard
          key={request.id}
          user={request.otherUser}
          actions={
            <>
              <Button
                size="sm"
                isLoading={accept.isPending && accept.variables === request.id}
                onClick={() => accept.mutate(request.id)}
              >
                Accept
              </Button>
              <Button
                variant="secondary"
                size="sm"
                isLoading={decline.isPending && decline.variables === request.id}
                onClick={() => decline.mutate(request.id)}
              >
                Decline
              </Button>
            </>
          }
        />
      ))}
      {accept.isError && <ErrorState message={getErrorMessage(accept.error)} />}
      {decline.isError && <ErrorState message={getErrorMessage(decline.error)} />}
    </div>
  );
}

function OutgoingRequestsSection() {
  const outgoing = useOutgoingFriendRequests();
  const cancel = useCancelFriendRequest();

  if (outgoing.isPending) return <LoadingState label="Loading requests…" />;
  if (outgoing.isError) {
    return (
      <ErrorState
        message={getErrorMessage(outgoing.error)}
        onRetry={() => void outgoing.refetch()}
      />
    );
  }
  if (outgoing.data.length === 0) {
    return <p className="text-textSecondary">No outgoing friend requests.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {outgoing.data.map((request) => (
        <UserCard
          key={request.id}
          user={request.otherUser}
          meta="Request pending"
          actions={
            <Button
              variant="secondary"
              size="sm"
              isLoading={cancel.isPending && cancel.variables === request.id}
              onClick={() => cancel.mutate(request.id)}
            >
              Cancel
            </Button>
          }
        />
      ))}
      {cancel.isError && <ErrorState message={getErrorMessage(cancel.error)} />}
    </div>
  );
}

function SearchSection() {
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());
  const search = useUserSearch(submittedQuery);
  const sendRequest = useSendFriendRequest();
  const blockUser = useBlockUser();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmittedQuery(query.trim());
  };

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={onSubmit} className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            label="Username"
            placeholder="Search by username"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <Button type="submit">Search</Button>
      </form>

      {search.isPending && submittedQuery.length > 0 && <LoadingState label="Searching…" />}
      {search.isError && <ErrorState message={getErrorMessage(search.error)} />}
      {search.isSuccess && submittedQuery.length > 0 && search.data.length === 0 && (
        <p className="text-textSecondary">No players found for &quot;{submittedQuery}&quot;.</p>
      )}
      {search.isSuccess && search.data.length > 0 && (
        <div className="flex flex-col gap-2">
          {search.data.map((result) => {
            const alreadySent = sentTo.has(result.user.username);
            return (
              <UserCard
                key={result.user.id}
                user={result.user}
                actions={
                  <>
                    <Button
                      size="sm"
                      disabled={alreadySent}
                      isLoading={
                        sendRequest.isPending && sendRequest.variables === result.user.username
                      }
                      onClick={() =>
                        sendRequest.mutate(result.user.username, {
                          onSuccess: () => {
                            setSentTo((prev) => new Set(prev).add(result.user.username));
                          },
                        })
                      }
                    >
                      {alreadySent ? 'Request sent' : 'Add friend'}
                    </Button>
                    <BlockConfirmButton
                      username={result.user.username}
                      isLoading={
                        blockUser.isPending && blockUser.variables === result.user.username
                      }
                      onConfirm={() => blockUser.mutateAsync(result.user.username)}
                    />
                  </>
                }
              />
            );
          })}
        </div>
      )}
      {sendRequest.isError && <ErrorState message={getErrorMessage(sendRequest.error)} />}
      {blockUser.isError && <ErrorState message={getErrorMessage(blockUser.error)} />}
    </div>
  );
}

/**
 * Blocked-users list with unblock (APP_SPEC §15, §65 "Blocking"). Lives
 * here, as the final Friends tab, so all relationship management is in one
 * place (docs/UI_GUIDELINES.md §5) — it was previously under Settings.
 */
function BlockedUsersSection() {
  const blocked = useBlockedUsers();
  const unblock = useUnblockUser();

  if (blocked.isPending) return <LoadingState label="Loading blocked users…" />;
  if (blocked.isError) {
    return (
      <ErrorState message={getErrorMessage(blocked.error)} onRetry={() => void blocked.refetch()} />
    );
  }
  if (blocked.data.length === 0) {
    return <p className="text-textSecondary">You haven&apos;t blocked anyone.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {blocked.data.map((entry) => (
        <UserCard
          key={entry.user.id}
          user={entry.user}
          meta={`Blocked ${formatDate(entry.blockedAt)}`}
          actions={
            <Button
              variant="secondary"
              size="sm"
              isLoading={unblock.isPending && unblock.variables === entry.user.id}
              onClick={() => unblock.mutate(entry.user.id)}
            >
              Unblock
            </Button>
          }
        />
      ))}
      {unblock.isError && <ErrorState message={getErrorMessage(unblock.error)} />}
    </div>
  );
}

/**
 * The friends screen (APP_SPEC §14, §65 "Friends"; docs/APP_PLAN.md §12
 * M4). Player search, sending/accepting/declining/cancelling friend
 * requests, removing a friend, blocking a search result, and managing
 * blocked users all live here — the one place for relationship management.
 * Every list is already block-filtered server-side
 * (docs/APP_PLAN.md §9.6) — this page renders whatever the API returns.
 */
export function FriendsPage() {
  const [tab, setTab] = useState('friends');
  const incoming = useIncomingFriendRequests();
  const incomingCount = incoming.data?.length ?? 0;

  return (
    <PageLayout>
      <PageHeader
        eyebrow="Fellow explorers"
        title="Friends"
        description="Find players and manage the people you share games with."
      />
      <Tabs
        aria-label="Friends"
        value={tab}
        onValueChange={setTab}
        items={[
          { value: 'friends', label: 'Friends', content: <FriendsListSection /> },
          {
            value: 'incoming',
            label: incomingCount > 0 ? `Incoming (${String(incomingCount)})` : 'Incoming',
            content: <IncomingRequestsSection />,
          },
          { value: 'outgoing', label: 'Outgoing', content: <OutgoingRequestsSection /> },
          { value: 'search', label: 'Find players', content: <SearchSection /> },
          { value: 'blocked', label: 'Blocked', content: <BlockedUsersSection /> },
        ]}
      />
    </PageLayout>
  );
}
